import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  TableModule,
  TableConfiguration,
} from '@lifeomic/chroma-react/components/TableModule';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { MapPin, Video2 } from '@lifeomic/chromicons';
import { findCoding, formatCodingToken } from '@lifeomic/fhir-constants';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import { fhirSystems } from '../fhir-systems';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fhirClient, useDataset } from '../../../../util/api';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';

const messages = defineMessages({
  type: {
    id: 'MemberAppointments.type',
    defaultMessage: 'Type',
  },
  startDate: {
    id: 'MemberAppointments.startDate',
    defaultMessage: 'Start Date',
  },
  locationType: {
    id: 'MemberAppointments.locationType',
    defaultMessage: 'Location Type',
  },
  inPerson: {
    id: 'MemberAppointments.inPerson',
    defaultMessage: 'In-Person',
  },
  virtual: {
    id: 'MemberAppointments.virtual',
    defaultMessage: 'Virtual',
  },
  status: {
    id: 'MemberAppointments.status',
    defaultMessage: 'Status',
  },
  booked: {
    id: 'MemberAppointments.booked',
    defaultMessage: 'Booked',
  },
  arrived: {
    id: 'MemberAppointments.arrived',
    defaultMessage: 'Arrived',
  },
  fulfilled: {
    id: 'MemberAppointments.fulfilled',
    defaultMessage: 'Fulfilled',
  },
  cancelled: {
    id: 'MemberAppointments.cancelled',
    defaultMessage: 'Cancelled',
  },
  noshow: {
    id: 'MemberAppointments.noshow',
    defaultMessage: 'No Show',
  },
  pending: {
    id: 'MemberAppointments.pending',
    defaultMessage: 'Pending',
  },
  proposed: {
    id: 'MemberAppointments.proposed',
    defaultMessage: 'Proposed',
  },
});

const useStyles = makeStyles(theme => ({
  locationIcon: {
    color: theme.palette.text.hint,
    minWidth: theme.pxToRem(20),
    minHeight: theme.pxToRem(20),
    width: theme.pxToRem(20),
    height: theme.pxToRem(20),
  },
  centerAlign: {
    textAlign: 'center',
  },
  tableScene: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    maxHeight: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
  },
  tableContainer: {
    flex: 1,
    maxHeight: 450,
    maxWidth: '100%',
    minHeight: 0,
    overflowY: 'scroll',
    overflowX: 'auto',
    width: '100%',
  },
}));

interface MemberAppointmentsProps {
  patientId: string;
}

export const MemberAppointments: React.FC<MemberAppointmentsProps> = ({
  patientId,
}) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const dataset = useDataset();

  const query = useInfiniteQuery({
    queryKey: ['appointments', patientId],
    queryFn: async ({ pageParam }) => {
      const response = await fhirClient.request(
        'GET /v1/fhir/dstu3/Appointment',
        {
          _tag: [
            dataset.codingToken,
            formatCodingToken({
              system: fhirSystems.source.system,
              code: fhirSystems.source.values.elationAppointment,
            }),
          ],
          patient: patientId,
          _sort: '-date',
          date: `ge1970-01-01`,
          pageSize: 25,
          next: pageParam,
        },
      );

      return {
        appointments: unwrapBundle(response.data),
        bundle: response.data,
      };
    },
    getNextPageParam: ({ bundle: lastBundle }) => {
      const nextLink = lastBundle.link?.find(link => link.relation === 'next');
      const params = new URLSearchParams(nextLink?.url);
      return params.get('next') || undefined;
    },
  });

  const appointments = useMemo(
    () => query.data?.pages.flatMap(page => page.appointments) || [],
    [query.data?.pages],
  );

  const tableConfig = useMemo<TableConfiguration[]>(() => {
    return [
      {
        header: {
          label: formatMessage(messages.type),
        },
        cell: {
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const elationReason = findCoding(appointment?.meta?.tag, {
              system: fhirSystems.appointment.elationReason.system,
            })?.code;

            return (
              <Text size="caption">
                {elationReason || appointment.description || '—'}
              </Text>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.status),
          className: styles.centerAlign,
        },
        cell: {
          className: styles.centerAlign,
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const statusMessages = {
              booked: messages.booked,
              arrived: messages.arrived,
              fulfilled: messages.fulfilled,
              cancelled: messages.cancelled,
              noshow: messages.noshow,
              pending: messages.pending,
              proposed: messages.proposed,
              'entered-in-error': messages.cancelled,
            };

            return (
              <Text size="caption">
                {formatMessage(
                  statusMessages[appointment.status] || appointment.status,
                )}
              </Text>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.startDate),
        },
        cell: {
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const startDate = appointment.start
              ? new Date(appointment.start).toLocaleDateString()
              : '—';
            const startTime = appointment.start
              ? new Date(appointment.start).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '';

            return (
              <Box direction="column">
                <Text size="caption">{startDate}</Text>
                {startTime && <Text size="caption">{startTime}</Text>}
              </Box>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.locationType),
          className: styles.centerAlign,
        },
        cell: {
          content: (appointment: WithPlatformDefaults<fhir3.Appointment>) => {
            const locationTypeCode = findCoding(appointment?.meta?.tag, {
              system: fhirSystems.appointment.locationType.system,
            })?.code;

            return (
              <Box justify="center" gap={1.5}>
                {locationTypeCode && (
                  <>
                    {locationTypeCode === 'in-person' ? (
                      <MapPin className={styles.locationIcon} />
                    ) : (
                      <Video2 className={styles.locationIcon} />
                    )}
                  </>
                )}
                <Text size="caption">
                  {locationTypeCode === 'in-person'
                    ? formatMessage(messages.inPerson)
                    : formatMessage(messages.virtual)}
                </Text>
              </Box>
            );
          },
        },
      },
    ];
  }, [formatMessage, styles.centerAlign, styles.locationIcon]);

  return (
    <div className={styles.tableContainer}>
      <InfiniteScroll
        scrollContainer="parent"
        loading={query.isFetchingNextPage}
        hasNextPage={Boolean(query.hasNextPage)}
        loadMore={() => query.fetchNextPage()}
        debounceInterval={1000}
        scrollOffset={250}
      >
        <div className={styles.tableScene}>
          <TableModule
            data={appointments}
            config={tableConfig}
            wrapCellContent
            isLoading={query.isLoading}
          />
        </div>
      </InfiniteScroll>
    </div>
  );
};
